import { percentageFormatter } from "../../../../mp-core/helpers/formatters"
import { Column } from "../../../../reporting/components/Table"
import {
  ContentInfo,
  StudentStudyPlanData
} from "../../../../../types/gqlTypes/graphql"
export const STUDENT_NAME_FIELD = "student.name"
export const STUDENT_COMPLETE_FIELD = "student.complete"
export const ORG_FILTER_KEYS = [
  "district",
  "school",
  "teacher",
  "grade",
  "class",
  "student"
]

export type ScoreIconMapping = {
  icon: string
  label: string
}

// Per-program mappings of text score values to icons and display labels
const SCORE_ICON_MAPPINGS: Record<string, Record<string, ScoreIconMapping>> = {
  CCRA: {
    "below basic": { icon: "circle-exclamation", label: "Below Basic" },
    basic: { icon: "arrow-circle-right", label: "Basic" },
    proficient: { icon: "circle-check", label: "Proficient" },
    advanced: { icon: "award", label: "Advanced" }
  },
  FLEOC: {
    "level 1 - inadequate": {
      icon: "circle-exclamation",
      label: "Level 1 - Inadequate"
    },
    "level 2 - below satisfactory": {
      icon: "arrow-circle-right",
      label: "Level 2 - Below Satisfactory"
    },
    "level 3 - on grade level": {
      icon: "circle-check",
      label: "Level 3 - On Grade Level"
    },
    "level 4 - above satisfactory": {
      icon: "check-double",
      label: "Level 4 - Above Satisfactory"
    },
    "level 5 - mastery": { icon: "award", label: "Level 5 - Mastery" }
  },
  TCAP: {
    "below expectations": {
      icon: "circle-exclamation",
      label: "Below Expectations"
    },
    "approaching expectations": {
      icon: "arrow-circle-right",
      label: "Approaching Expectations"
    },
    "meets expectations": { icon: "circle-check", label: "Meets Expectations" },
    "exceeds expectations": { icon: "award", label: "Exceeds Expectations" }
  },
  EOCEP: {
    "does not meet": { icon: "circle-exclamation", label: "Does Not Meet" },
    "minimally meets": { icon: "arrow-circle-right", label: "Minimally Meets" },
    meets: { icon: "circle-check", label: "Meets" },
    exceeds: { icon: "award", label: "Exceeds" }
  },
  STAAR: {
    "did not meet": { icon: "circle-exclamation", label: "Did Not Meet" },
    approaches: { icon: "arrow-circle-right", label: "Approaches" },
    meets: { icon: "circle-check", label: "Meets" },
    masters: { icon: "award", label: "Masters" }
  },
  KSA: {
    novice: { icon: "circle-exclamation", label: "Novice" },
    apprentice: { icon: "arrow-circle-right", label: "Apprentice" },
    proficient: { icon: "circle-check", label: "Proficient" },
    distinguished: { icon: "award", label: "Distinguished" }
  },
  LEAP: {
    unsatisfactory: { icon: "circle-exclamation", label: "Unsatisfactory" },
    "approaching basic": {
      icon: "arrow-circle-right",
      label: "Approaching Basic"
    },
    basic: { icon: "circle-check", label: "Basic" },
    mastery: { icon: "check-double", label: "Mastery" },
    advanced: { icon: "award", label: "Advanced" }
  },
  MAAP: {
    minimal: { icon: "circle-exclamation", label: "Minimal" },
    basic: { icon: "arrow-circle-right", label: "Basic" },
    passing: { icon: "circle-check", label: "Passing" },
    proficient: { icon: "check-double", label: "Proficient" },
    advanced: { icon: "award", label: "Advanced" }
  },
  NCEOC: {
    "not proficient": { icon: "circle-exclamation", label: "Not Proficient" },
    "level 3": { icon: "arrow-circle-right", label: "Level 3" },
    "level 4": { icon: "circle-check", label: "Level 4" },
    "level 5": { icon: "award", label: "Level 5" }
  }
}

export const getScoreIconMapping = (
  program: string,
  value: any
): ScoreIconMapping | null => {
  if (value == null || typeof value !== "string") return null
  const programMap = SCORE_ICON_MAPPINGS[program?.toUpperCase()]
  if (!programMap) return null
  return programMap[value.toLowerCase().trim()] ?? null
}

export const getScoresByStudentTableHeaders = (
  responseHeaders: ContentInfo[]
): Column[] => {
  const baseHeaders: Column[] = [
    {
      size: 180,
      name: STUDENT_NAME_FIELD,
      label: "Student",
      sortable: false // TODO: Enable when backend supports sorting
    },
    {
      size: 130,
      align: "right",
      name: STUDENT_COMPLETE_FIELD,
      label: "% Complete",
      type: "horizontalProgress",
      typeProp: STUDENT_COMPLETE_FIELD,
      sortable: false, // TODO: Enable when backend supports sorting
      diagonalHeader: true,
      formatter: (record, prop) => {
        return percentageFormatter.format((record[prop] || 0) / 100)
      }
    }
  ]

  const transformedResponseHeaders: Column[] =
    responseHeaders?.map(item => {
      return {
        size: 60,
        align: "right",
        name: item.id,
        label: item.name,
        sortable: false, // TODO: Enable when backend supports sorting
        sortableName: item.id,
        diagonalHeader: true
      }
    }) || []

  return [...baseHeaders, ...transformedResponseHeaders]
}

export const getScoresByStudentTableRecords = (
  studentsData: StudentStudyPlanData[]
) => {
  return studentsData?.map(student => {
    const transformedStudentData =
      student.data?.reduce((res, item) => {
        return {
          ...res,
          [item.id]: item.value || item.pass
        }
      }, {}) || {}

    return {
      id: student.id,
      [STUDENT_NAME_FIELD]: student.name,
      [STUDENT_COMPLETE_FIELD]: student.completed ?? 0,
      ...transformedStudentData
    }
  })
}
